import { returnStatuses } from '@reveni-io/reveni-ui'

import { paymentStatuses, refundStatuses, transactionTypes } from '@/returns/constants'

import {
  acceptedStatuses,
  exchangeTransactionTypes,
  messages,
  paymentOnAcceptExcludedStatuses,
  refundInProgressStatuses,
  rejectedOrPartiallyAcceptedStatuses,
  titles,
} from './constants'

const isExchangePaymentPending = ({ transactionType, summaryTotalAmount }) => {
  return exchangeTransactionTypes.includes(transactionType) && Number(summaryTotalAmount) < 0
}

const hasPendingExchangePayment = params => {
  return isExchangePaymentPending(params) && params.paymentStatus === paymentStatuses.pending
}

const isTransactionPending = ({ transactionStatus }) => transactionStatus === returnStatuses.pending

const isPaymentOnAccept = params => {
  return (
    !params.isInstant &&
    !params.exchangedForOrder &&
    hasPendingExchangePayment(params) &&
    !paymentOnAcceptExcludedStatuses.includes(params.transactionStatus)
  )
}

const isExchangeOrderPaymentPending = params => {
  return (
    !params.isInstant &&
    params.exchangedForOrder &&
    hasPendingExchangePayment(params) &&
    acceptedStatuses.includes(params.transactionStatus)
  )
}

const requiresManualPaymentWhenIssuedAtAccepted = params => {
  const { exchangedForOrder, transactionStatus } = params
  const isAcceptedWithoutExchangeOrder = !exchangedForOrder && acceptedStatuses.includes(transactionStatus)
  const isRejected = transactionStatus === returnStatuses.rejected

  return (
    params.issueExchangeAtStatus === returnStatuses.accepted &&
    hasPendingExchangePayment(params) &&
    (isAcceptedWithoutExchangeOrder || isRejected)
  )
}

const requiresManualPaymentWhenIssuedAtInProgress = params => {
  return (
    params.issueExchangeAtStatus === returnStatuses.inProgress &&
    isExchangePaymentPending(params) &&
    rejectedOrPartiallyAcceptedStatuses.includes(params.transactionStatus)
  )
}

const requiresManualRefundPayment = ({ transactionType, remainingAmount, transactionStatus }) => {
  return (
    transactionType === transactionTypes.refund &&
    Number(remainingAmount) < 0 &&
    rejectedOrPartiallyAcceptedStatuses.includes(transactionStatus)
  )
}

const requiresManualPayment = params => {
  if (params.isInstant) return false

  return (
    requiresManualPaymentWhenIssuedAtAccepted(params) ||
    requiresManualPaymentWhenIssuedAtInProgress(params) ||
    requiresManualRefundPayment(params)
  )
}

const isRefundPending = ({ isInstant, refundStatus, transactionStatus }) => {
  return !isInstant && refundStatus === refundStatuses.pending && refundInProgressStatuses.includes(transactionStatus)
}

const requiresManualRefund = ({ isInstant, refundStatus, transactionStatus }) => {
  return !isInstant && refundStatus === refundStatuses.pending && acceptedStatuses.includes(transactionStatus)
}

const isInstantRefundOnHold = ({ isInstant, refundStatus, transactionStatus }) => {
  return isInstant && refundStatus === refundStatuses.pending && transactionStatus === returnStatuses.onHold
}

const isPaymentOnHold = params => {
  return (
    hasPendingExchangePayment(params) &&
    params.transactionStatus === returnStatuses.onHold &&
    params.issueExchangeAtStatus === returnStatuses.inProgress
  )
}

const titleAndMessageRules = [
  { isMatch: isTransactionPending, title: titles.payment, message: messages.paymentPendingWithLink },
  { isMatch: isPaymentOnAccept, title: titles.payment, message: messages.paymentOnAccept },
  { isMatch: isExchangeOrderPaymentPending, title: titles.payment, message: messages.paymentPendingWithLink },
  { isMatch: requiresManualPayment, title: titles.payment, message: messages.manualPayment },
  { isMatch: isRefundPending, title: titles.refund, message: messages.refundPending },
  { isMatch: requiresManualRefund, title: titles.refund, message: messages.manualRefund },
  { isMatch: isInstantRefundOnHold, title: titles.refund, message: messages.instantRefundOnHold },
  { isMatch: isPaymentOnHold, title: titles.payment, message: messages.paymentOnHold },
]

export const getTitleAndMessage = params => {
  const rule = titleAndMessageRules.find(({ isMatch }) => isMatch(params))

  if (!rule) return { title: null, message: null }

  return { title: rule.title, message: rule.message }
}
